// Settings › Branding — the SEO & Social editor.
//
// Previously a 270-line tab called "SEO". On the Branding page the sheet shows the resulting
// search snippet and its character counts, and Edit opens this. The name covers what the
// fields actually govern: the browser tab title, the Google result, and the unfurl when
// someone drops a link in Slack. "Link previews" was only the third of those.
//
// Field names and the tenant_theme_settings.custom_seo shape are untouched; this is a
// relocation and a rename, not a rewrite.

import { useEffect, useState } from 'react';
import { Check, Loader2 } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import type { SEOConfig } from '@/types/theme';

interface SeoSocialDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  /** Current values, already merged from custom_seo → theme.seo → defaults by the caller. */
  value: SEOConfig;
  canEdit: boolean;
  onSave: (seo: SEOConfig) => Promise<void>;
}

const textareaClass =
  'w-full min-h-[80px] px-3 py-2 border border-input bg-background text-sm ring-offset-background placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50 rounded-md';

export function SeoSocialDialog({ open, onOpenChange, value, canEdit, onSave }: SeoSocialDialogProps) {
  const [draft, setDraft] = useState<SEOConfig>(value);
  const [dirty, setDirty] = useState(false);
  const [saving, setSaving] = useState(false);

  // Re-seed from props whenever the dialog opens, so a cancelled edit does not persist as
  // local state and reappear the next time it is opened.
  useEffect(() => {
    if (open) {
      setDraft(value);
      setDirty(false);
    }
  }, [open, value]);

  const set = (field: keyof SEOConfig, v: string) => {
    setDraft((prev) => ({ ...prev, [field]: v }));
    setDirty(true);
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      await onSave(draft);
      setDirty(false);
      onOpenChange(false);
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-h-[90vh] max-w-3xl overflow-y-auto">
        <DialogHeader>
          <DialogTitle>SEO &amp; Social</DialogTitle>
          <DialogDescription>
            The browser tab title, the Google result, and what appears when someone shares a link.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-6">
          <div className="space-y-4">
            <h3 className="text-sm font-semibold text-foreground">Basic</h3>

            <div className="space-y-2">
              <Label htmlFor="page-title">Page Title</Label>
              <Input
                id="page-title"
                placeholder="Enter page title (recommended: 50-60 characters)"
                value={draft.pageTitle || ''}
                onChange={(e) => set('pageTitle', e.target.value)}
                disabled={!canEdit}
              />
              <p className="text-sm text-muted-foreground">{draft.pageTitle?.length || 0}/60 characters</p>
            </div>

            <div className="space-y-2">
              <Label htmlFor="meta-description">Meta Description</Label>
              <textarea
                id="meta-description"
                placeholder="Enter meta description (recommended: 150-160 characters)"
                value={draft.metaDescription || ''}
                onChange={(e) => set('metaDescription', e.target.value)}
                disabled={!canEdit}
                className={textareaClass}
              />
              <p className="text-sm text-muted-foreground">
                {draft.metaDescription?.length || 0}/160 characters
              </p>
            </div>

            <div className="space-y-2">
              <Label htmlFor="meta-keywords">Meta Keywords</Label>
              <Input
                id="meta-keywords"
                placeholder="Enter keywords separated by commas"
                value={draft.metaKeywords || ''}
                onChange={(e) => set('metaKeywords', e.target.value)}
                disabled={!canEdit}
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="canonical-url">Canonical URL</Label>
              <Input
                id="canonical-url"
                type="url"
                placeholder="https://example.com"
                value={draft.canonicalUrl || ''}
                onChange={(e) => set('canonicalUrl', e.target.value)}
                disabled={!canEdit}
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="robots-meta">Robots Meta Tag</Label>
              <Select
                value={draft.robotsMeta || 'index, follow'}
                onValueChange={(v) => set('robotsMeta', v)}
                disabled={!canEdit}
              >
                <SelectTrigger id="robots-meta">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="index, follow">Index, Follow</SelectItem>
                  <SelectItem value="noindex, follow">No Index, Follow</SelectItem>
                  <SelectItem value="index, nofollow">Index, No Follow</SelectItem>
                  <SelectItem value="noindex, nofollow">No Index, No Follow</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>

          <div className="space-y-4">
            <h3 className="text-sm font-semibold text-foreground">Open Graph (social media)</h3>

            <div className="space-y-2">
              <Label htmlFor="og-title">OG Title</Label>
              <Input
                id="og-title"
                placeholder="Enter Open Graph title"
                value={draft.ogTitle || ''}
                onChange={(e) => set('ogTitle', e.target.value)}
                disabled={!canEdit}
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="og-description">OG Description</Label>
              <textarea
                id="og-description"
                placeholder="Enter Open Graph description"
                value={draft.ogDescription || ''}
                onChange={(e) => set('ogDescription', e.target.value)}
                disabled={!canEdit}
                className={textareaClass}
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="og-image">OG Image URL</Label>
              <Input
                id="og-image"
                type="url"
                placeholder="https://example.com/image.jpg"
                value={draft.ogImage || ''}
                onChange={(e) => set('ogImage', e.target.value)}
                disabled={!canEdit}
              />
              <p className="text-sm text-muted-foreground">Recommended: 1200x630px</p>
            </div>
          </div>

          <div className="space-y-4">
            <h3 className="text-sm font-semibold text-foreground">Twitter card</h3>

            <div className="space-y-2">
              <Label htmlFor="twitter-card">Twitter Card Type</Label>
              <Select
                value={draft.twitterCard || 'summary_large_image'}
                onValueChange={(v) => set('twitterCard', v)}
                disabled={!canEdit}
              >
                <SelectTrigger id="twitter-card">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="summary">Summary</SelectItem>
                  <SelectItem value="summary_large_image">Summary Large Image</SelectItem>
                  <SelectItem value="app">App</SelectItem>
                  <SelectItem value="player">Player</SelectItem>
                </SelectContent>
              </Select>
            </div>

            <div className="space-y-2">
              <Label htmlFor="twitter-title">Twitter Title</Label>
              <Input
                id="twitter-title"
                placeholder="Enter Twitter title"
                value={draft.twitterTitle || ''}
                onChange={(e) => set('twitterTitle', e.target.value)}
                disabled={!canEdit}
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="twitter-description">Twitter Description</Label>
              <textarea
                id="twitter-description"
                placeholder="Enter Twitter description"
                value={draft.twitterDescription || ''}
                onChange={(e) => set('twitterDescription', e.target.value)}
                disabled={!canEdit}
                className={textareaClass}
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="twitter-image">Twitter Image URL</Label>
              <Input
                id="twitter-image"
                type="url"
                placeholder="https://example.com/twitter-image.jpg"
                value={draft.twitterImage || ''}
                onChange={(e) => set('twitterImage', e.target.value)}
                disabled={!canEdit}
              />
              <p className="text-sm text-muted-foreground">Recommended: 1200x600px</p>
            </div>
          </div>

          <div className="space-y-2 rounded-lg bg-muted/50 p-4">
            <h4 className="text-sm font-medium text-muted-foreground">Search result preview</h4>
            <div className="truncate text-sm font-medium text-primary">{draft.pageTitle || 'Page Title'}</div>
            <div className="text-sm text-green-600 dark:text-green-400">
              {draft.canonicalUrl || 'https://example.com'}
            </div>
            <div className="text-sm text-muted-foreground">
              {draft.metaDescription || 'Meta description will appear here...'}
            </div>
          </div>

          <div className="flex items-center justify-end gap-3 border-t pt-4">
            {dirty && <span className="text-sm text-muted-foreground">You have unsaved changes</span>}
            <Button variant="outline" onClick={() => onOpenChange(false)} disabled={saving}>
              Cancel
            </Button>
            <Button onClick={handleSave} disabled={!canEdit || !dirty || saving} className="min-w-[100px]">
              {saving ? (
                <>
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                  Saving...
                </>
              ) : (
                <>
                  <Check className="mr-2 h-4 w-4" />
                  Save
                </>
              )}
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}
